import { css } from '@emotion/react'
import { Contentful, contentful } from 'api/contentful/api'
import { ContentfulFAQ, getContentfulFAQ } from 'api/contentful/getContentfulFAQ'
import {
  ContentfulMediaMentions,
  getContentfulMediaMentions,
} from 'api/contentful/getContentfulMediaMention'
import { Block } from 'components/Block'
import { Button } from 'components/button/Button'
import { BasicTextImageSection } from 'components/content/basicTextImageSection/BasicTextImageSection'
import { HelpCarousel } from 'components/content/helpCarousel/HelpCarousel'
import { HomepageCallToAction } from 'components/content/homepage/homepageCallToAction/HomepageCallToAction'
import { LifeStoriesContextProvider } from 'components/content/lifeStories/LifeStoriesContext'
import { MediaMentionsRow } from 'components/content/mediaMentions/MediaMentionsRow'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { Footer } from 'components/footer/Footer'
import { FutureMessageHero } from 'components/futureMessage/FutureMessageHero/FutureMessageHero'
import { FutureMessageInspiration } from 'components/futureMessage/futureMessageInspiration/FutureMessageInspiration'
import { FutureMessageLPHow } from 'components/futureMessage/futureMessageLPHow/FutureMessageLPHow'
import { FutureMessageLPWhen } from 'components/futureMessage/futureMessageLPWhen/FutureMessageLPWhen'
import { FutureMessageSteps } from 'components/futureMessage/futureMessageSteps/FutureMessageSteps'
import { useFutureMessageTemplateModalWithRedirectParams } from 'components/futureMessage/futureMessageTemplateModal/FutureMessageTemplateModal'
import { useFutureMessageStorage } from 'components/futureMessage/FutureMessageUtils'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { Icon } from 'components/icon'
import { Layout } from 'components/layout/Layout'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { Spacer } from 'components/spacer/Spacer'
import { Stack } from 'components/Stack'
import { TranslatableLink } from 'components/TranslatableLink'
import { Text, Title, Title_h3_default, Title_h4_default } from 'components/Typography'
import { GetStaticProps } from 'next'
import { Trans, useTranslation } from 'next-i18next'
import Router from 'next/router'
import { memoriesHomepageImages } from 'pages/memories'
import { useEffect } from 'react'
import { fireEvent, mutateAnalyticsData } from 'utils/analytics'
import { useAuth } from 'utils/auth/hooks/useAuth'
import { REVALIDATE_SECS } from 'utils/constants'
import { useContentfulImages } from 'utils/contentful/ContentfulImagesContext'
import { getContentfulImages } from 'utils/contentful/getPageImages'
import { numberRange } from 'utils/general'
import { useAuthAwareFetcher, useWindowSize } from 'utils/hooks'
import { getIntlProps } from 'utils/i18n/getIntlProps'
import { BREAKPOINTS } from 'utils/styled'
import { Await } from 'utils/types'

export default function Page(props: {
  futureMessageSetup: Await<Contentful<'getFutureMessageSetup'>>
  mediaMentions: ContentfulMediaMentions
  faq: ContentfulFAQ
}) {
  const { t } = useTranslation('future_message')
  const images = useContentfulImages()

  const { isLogged } = useAuth()
  useEffect(() => {
    mutateAnalyticsData({ page: 'future_message' })
  }, [])

  const fetcher = useAuthAwareFetcher()
  const { messageUuid } = useFutureMessageStorage()
  const { isDesktop } = useWindowSize()

  const [templateModal, openTemplateModal] = useFutureMessageTemplateModalWithRedirectParams()

  const onCreateOrContinue = async () => {
    if (messageUuid) {
      fireEvent('fm_continue')
      await Router.push(`/me/future-message/${messageUuid}`)
    } else {
      openTemplateModal({
        fetcher,
        type: 'landing',
        redirectParams: isLogged ? undefined : { shouldOnboard: true },
      })
    }
  }

  return (
    <LifeStoriesContextProvider>
      <Layout
        header={<TopNavigationLandingPage variant="full" />}
        footer={<Footer variant="full" />}
      >
        {templateModal}
        <PageTitle title={t('future_message.meta_title')} />
        <PageMeta
          description={t('future_message.meta_description')}
          image="/images/preview/future-messages.png"
        />

        <FutureMessageHero
          futureMessageSetup={props.futureMessageSetup}
          title={t('future_message.hero.title')}
          desc={t('future_message.hero.desc')}
          buttonText={
            messageUuid ? t('future_message.hero.cta_continue') : t('future_message.hero.cta')
          }
          onAction={onCreateOrContinue}
        />

        <Block>
          <MediaMentionsRow
            mediaMentions={props.mediaMentions}
            hideQuotes
            title={t('future_message.hero.mentions_caption')}
            alignCenterItems
            logoMaxHeightInRem={3}
            limit={5}
          />
        </Block>

        <Spacer size={[2, 6]} />

        <Block>
          <FutureMessageSteps
            title={
              isDesktop
                ? t('future_message.future_message_steps.desktop_title')
                : t('future_message.future_message_steps.mobile_title')
            }
          />
        </Block>

        <Spacer size={[4, 8]} />

        <BasicTextImageSection
          orientation="right"
          title={t('future_message.section_1.title')}
          desc={t('future_message.section_1.description')}
          image={images.future_message_section_1}
          imageSubtitle={t('future_message.section_1.image_subtitle')}
          actionTitle={t('future_message.section_1.action_title')}
          actionIcon={<Icon name="Send" />}
          onAction={onCreateOrContinue}
        />

        <Spacer size={[2, 7]} />

        <BasicTextImageSection
          title={t('future_message.section_2.title')}
          desc={t('future_message.section_2.description')}
          image={images.future_message_section_2}
          imageSubtitle={t('future_message.section_2.image_subtitle')}
          actionTitle={t('future_message.section_2.action_title')}
          actionIcon={<Icon name="Send" />}
          onAction={onCreateOrContinue}
        />

        <Spacer size={[2, 7]} />

        <BasicTextImageSection
          orientation="right"
          title={t('future_message.section_3.title')}
          desc={t('future_message.section_3.description')}
          image={images.future_message_section_3}
          imageSubtitle={t('future_message.section_3.image_subtitle')}
          actionTitle={t('future_message.section_3.action_title')}
          actionIcon={<Icon name="Key" />}
          onAction={() => void Router.push(`/security`)}
        />

        <Spacer size={[3, 7]} />

        <FutureMessageInspiration
          onAction={onCreateOrContinue}
          inspiredList={props.futureMessageSetup?.futureMessageCollection?.items?.[0]?.inspiredList}
        />

        <Spacer size={[5, 7]} />

        <Block>
          <Stack space={`1.375rem`} css={{ alignItems: 'center', textAlign: 'center' }}>
            <Title variant={Title_h3_default} as={'h3'} css={{ maxWidth: '32rem' }}>
              {t('future_message.centered_text_section.title')}
            </Title>
            <Text css={{ maxWidth: '44rem' }}>
              {t('future_message.centered_text_section.description_1')}
            </Text>
            <Text> {t('future_message.centered_text_section.description_2')}</Text>
          </Stack>
        </Block>

        <Spacer size={[5, 7]} />

        <BasicTextImageSection
          orientation="right"
          title={t('future_message.example_section.title')}
          titleVariant={Title_h4_default}
          desc={
            <>
              {t('future_message.example_section.description')}
              <ul css={{ marginTop: '0', paddingBottom: '1rem' }}>
                {numberRange(7).map((i) => (
                  <li key={i}>{t(`future_message.example_section.example_${i}`)}</li>
                ))}
              </ul>
              <Text variant={'bodySmall'}>{t('future_message.example_section.footer')}</Text>
            </>
          }
          descNoMaxWidth
          image={images.future_message_section_4}
        />

        <Spacer size={[4, 7]} />

        <FutureMessageLPWhen />

        <Spacer size={[3, 7]} />

        <FutureMessageLPHow />

        <Button
          type="button"
          css={{ margin: '0 auto' }}
          onClick={onCreateOrContinue}
          size="large"
          leftIcon={<Icon name="Send" />}
        >
          {t('future_message.how_to_create_section.cta')}
        </Button>

        <Spacer size={[4, 7]} />

        <FaqTwoColumns
          content={props.faq}
          title={isDesktop ? t('future_message.faq_title') : undefined}
          titleVariant={Title_h3_default}
          titleCss={css`
            @media ${BREAKPOINTS.MD.min} {
              max-width: 100%;
              text-align: center;
            }
          `}
        />

        <Spacer size={[4, 7]} />

        <HelpCarousel />

        <Spacer size={[4, 7]} />

        <HomepageCallToAction
          actionTitle={t('future_message.cta_section.cta')}
          title={t('future_message.cta_section.title')}
          images={memoriesHomepageImages}
          description={
            <Trans
              t={t}
              i18nKey="future_message.cta_section.description"
              components={[
                <TranslatableLink key="0" variant="primary" href="/end-of-life-guide" />,
              ]}
            />
          }
        />

        <Spacer size={[2, 8]} />
      </Layout>
    </LifeStoriesContextProvider>
  )
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const futureMessageSetup = await contentful.getFutureMessageSetup()
  const mediaMentions = await getContentfulMediaMentions('global')
  const faq = await getContentfulFAQ('future-message')

  return {
    props: {
      ...(await getContentfulImages()),
      ...(await getIntlProps(ctx, ['future_message', 'memory', 'life_stories'])),
      futureMessageSetup,
      mediaMentions,
      faq,
    },
    revalidate: REVALIDATE_SECS,
  }
}
